'use client';

/**
 * `useClaimBackerFees` — SDK-owned mutation for the burn-to-claim
 * backer-fee vault withdrawal (post-resolution / graduated markets only).
 *
 * Mirrors the shape of `useBoostTrade`: reads host bindings from
 * `<TradingHostProvider>` via `useTradingHostContext()`, builds the
 * tx via `@morfi/contract-sdk.buildClaimBackerFees`, signs + submits
 * via `deps.submitTx`. Same toast / telemetry contract.
 */
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { reconcileBackerRewardAfterClaim } from '../../../core/internal/backer-reward-reconcile.js';
import { announceConfirmedClaim } from './helpers/claim-success.js';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showCancelledToast, showClaimSubmittingToast, showFailureToast } from './helpers/toasts.js';
import { generateTraceId } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
export function useClaimBackerFees() {
  const deps = useTradingHostContext();
  const abortControllerRef = useRef(null);
  const mutation = useMutation({
    mutationFn: async params => {
      if (params.signingDeps === undefined) {
        throw new Error('signingDeps is required for claim transactions');
      }
      const signingDeps = params.signingDeps;
      const traceId = generateTraceId();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const startedAt = Date.now();
      deps.txSubmission.startSubmission(traceId, 'claim_backer_fees');
      showClaimSubmittingToast(deps, traceId, abortController);
      try {
        const {
          BN,
          buildClaimBackerFees
        } = await import('@morfi/contract-sdk');
        const {
          PublicKey
        } = await import('@solana/web3.js');
        deps.txSubmission.updateStage(traceId, 'signing');
        const connection = deps.getConnection();
        const sdkResult = await buildClaimBackerFees(connection, {
          claimer: new PublicKey(params.activeWallet.address),
          market: new PublicKey(params.marketAddress),
          burnAmount: new BN(params.burnAmountD6)
        });
        const result = await deps.submitTx({
          instructions: sdkResult.instructions,
          signers: sdkResult.signerKeys
        }, {
          instructionName: 'claim_backer_fees',
          traceId,
          signingDeps,
          activeWallet: params.activeWallet,
          turnkeyDeps: deps.turnkeyDeps,
          signal: abortController.signal
        });
        const claimCtx = {
          traceId,
          instruction: 'claim_backer_fees',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: 0,
          latencyMs: Date.now() - startedAt
        };
        if (result.ok) {
          deps.txSubmission.setSignature(traceId, result.signature);
          deps.txSubmission.updateStage(traceId, 'confirmed');
          void announceConfirmedClaim(deps, {
            kind: 'claim_backer_fees',
            traceId,
            signature: result.signature,
            marketAddress: params.marketAddress,
            walletAddress: params.activeWallet.address
          });
          trackTxSuccess(deps, claimCtx, result.signature);
        } else if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
        } else {
          deps.txSubmission.setError(traceId, {
            code: result.error.code,
            userMessageKey: result.error.userMessageKey
          });
          showFailureToast(deps, traceId, result.error);
          trackTxFailure(deps, claimCtx, result.error);
        }
        return result;
      } catch (err) {
        if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
          return {
            ok: false,
            error: {
              code: 'TX_CANCELLED',
              stage: 'preflight',
              userMessageKey: 'tx.errors.cancelled',
              traceId,
              recoveryAction: {
                type: 'not_retryable'
              }
            },
            traceId
          };
        }
        deps.txSubmission.setError(traceId, {
          code: 'TX_UNKNOWN',
          userMessageKey: 'tx.errors.unknown'
        });
        showFailureToast(deps, traceId, {
          code: 'TX_UNKNOWN',
          stage: 'preflight',
          userMessageKey: 'tx.errors.unknown',
          traceId,
          recoveryAction: {
            type: 'prompt_retry'
          }
        });
        trackTxException(deps, {
          traceId,
          instruction: 'claim_backer_fees',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: 0,
          latencyMs: Date.now() - startedAt
        }, err);
        throw err;
      }
    },
    onSettled: (data, _error, params_0) => {
      if (data?.ok === true) {
        void reconcileBackerRewardAfterClaim(deps.queryClient, params_0.marketAddress);
      }
      deps.onMutationSettled('claim_backer_fees', {
        marketAddress: params_0.marketAddress,
        walletAddress: params_0.activeWallet.address
      });
    }
  });
  return {
    ...mutation,
    abortControllerRef
  };
}